import * as React from 'react'

/* TYPES */

interface BufferConfig {
  initialValue: string
}

interface BufferApi {
  clear: () => void
  reset: () => void
  update: (value: string) => void
  value: string
}

interface Props extends BufferConfig {
  children?: React.ReactNode
}

interface RenderProps extends BufferConfig {
  children: (props: BufferApi) => {}
}

/* HOOK */

export const useBuffer = ({ initialValue = '' }: BufferConfig): BufferApi => {
  const [value, setValue] = React.useState(initialValue)

  const clear = () => setValue('')

  const reset = () => setValue(initialValue)

  const update = (value: string) => setValue(value)

  return {
    clear,
    reset,
    update,
    value,
  }
}

/* DECORATOR */

export const withBuffer = <T extends {}>(config: BufferConfig) => (
  Component: React.ComponentType<T>
) => (props: T & { buffer: BufferConfig }) => (
  <Component {...props} buffer={useBuffer({ ...config, ...props.buffer })} />
)

/* RENDER PROP */

export const Buffer = ({ children, ...props }: RenderProps) => {
  const buffer = useBuffer(props)

  return typeof children === 'function' ? children(buffer) : children
}

/* CONTEXT */

const noop = (method: string) => () =>
  console.warn(
    `Could not call \`${method}\`, please wrap your component in a \`<BufferProvider>\``
  )

export const BufferContext = React.createContext<BufferApi>({
  value: '',
  clear: noop('clear'),
  reset: noop('reset'),
  update: noop('update'),
})

export const BufferProvider = ({ children, ...props }: Props) => (
  <BufferContext.Provider value={useBuffer(props)}>
    {children}
  </BufferContext.Provider>
)

export const consumeBuffer = () => React.useContext(BufferContext)
